export const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      console.log('Text copied to clipboard');
    })
    .catch((err) => {
      console.error('Failed to copy text: ', err);
    });
};

export const shortenWalletAddress = (
  walletAddress: string,
  startChars: number = 4,
  endChars: number = 4
): string => {
  if (walletAddress.length <= startChars + endChars) {
    return walletAddress;
  }

  const start = walletAddress.substring(0, startChars);
  const end = walletAddress.substring(walletAddress.length - endChars);

  return `${start}...${end}`;
};

export const shareContent = async (
  title: string,
  text: string,
  url: string
) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: title,
        text: text,
        url: url,
      });
      console.log('Share successful');
    } catch (error) {
      console.error('Error sharing:', error);
    }
  } else {
    console.log('Web Share API not supported');
  }
};

export const convertTimestampToTime = (timestamp: number | string) => {
  const date = new Date(timestamp);

  let hours = date.getHours();
  const minutes = date.getMinutes();

  const ampm = hours >= 12 ? 'PM' : 'AM';

  hours = hours > 12 ? hours - 12 : hours;

  const result = hours + ':' + minutes + ' ' + ampm;

  return result;
};

export const formatNumber = (number: number, digits = 2) => {
  // Convert the number to a string with commas for thousands
  if (number % 1 !== 0) {
    return number?.toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
  return number?.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export const formatWeight = (weight: number) => {
  if (weight >= 1000) {
    return `${weight / 1000}kg`;
  }
  return `${weight}Gram`;
};

export const toFixedNumber = (num: number, fixed = 3): number => {
  if (!num) return 0;
  if (num % 1 !== 0) {
    return +num.toFixed(fixed);
  }
  return +num;
};

export const formatDate = (timestamp: number | string) => {
  const date = new Date(timestamp);

  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();

  // Format the date as "DD Mon YYYY"
  const formattedDate = `${day.toString().padStart(2, '0')} ${month} ${year}`;

  return formattedDate;
};

export function isBeforeNow(date: string | number): boolean {
  const fixedDate = new Date();

  const inputDate = new Date(date);

  return inputDate < fixedDate;
}

export function getDaysFrom(dateString: string): number {
  const date = new Date(dateString);
  const now = new Date();

  // Calculate the time difference in milliseconds
  const diffInMs = now.getTime() - date.getTime();

  // Convert milliseconds to days
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return Math.floor(diffInDays); // Round down to the nearest whole day
}

export function withdrawActiveCheck(days: string[]): boolean {
  const today = new Date();
  const day = today.getDate();

  return days.includes(day.toString());
}

export function getWithdrawTime(days: string[]): Date {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // Current month (0-based index)

  // Get the next withdrawal date
  // days are strings, find the closest day in the future
  const nextWithdrawDay = days
    .map((day) => {
      // Find the next date of the week
      const dayOfWeek = new Date(year, month, +day);
      if (dayOfWeek < today) {
        dayOfWeek.setMonth(dayOfWeek.getMonth() + 1);
      }
      return dayOfWeek;
    })
    .sort((a, b) => a.getTime() - b.getTime())[0];

  return nextWithdrawDay;
}

export function getCompletionPercentage(
  currentLeftAmount: number,
  currentRightAmount: number,
  target: number
) {
  const maxLegPercentage = 100;

  let leftPercentage = Math.min(
    (currentLeftAmount / target) * 100,
    maxLegPercentage
  );
  let rightPercentage = Math.min(
    (currentRightAmount / target) * 100,
    maxLegPercentage
  );

  return (leftPercentage + rightPercentage) / 2;
}

export function formatTimestampToDateTimeArray(
  timestamp: number
): [string, string] {
  const date = new Date(timestamp);

  const formattedDate = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date);

  return [formattedDate, formattedTime];
}

export const truncateAndFormat = (number: number, digits = 2) => {
  if (number === null || number === undefined) {
    return '0';
  }

  // 1. Truncate the number mathematically
  const factor = Math.pow(10, digits);
  const truncatedNumber = Math.trunc(number * factor) / factor;

  // 2. Separate into parts
  const numString = truncatedNumber.toString();
  const parts = numString.split('.');
  const integerPart = parts[0];
  let decimalPart = parts.length > 1 ? parts[1] : '';

  // 3. Format the integer part with commas
  const formattedIntegerPart = Number(integerPart).toLocaleString('en-US');

  // 4. Handle decimal part logic
  if (digits > 0) {
    // Pad/Slice the decimal part to the required length
    decimalPart = decimalPart.padEnd(digits, '0').slice(0, digits);

    // Check if the padded decimal part consists only of zeros
    const isZeroDecimal = /^[0]+$/.test(decimalPart);

    if (isZeroDecimal) {
      // If the decimal part is all zeros (e.g., input 1234.000)
      return formattedIntegerPart;
    } else {
      // If the decimal part contains non-zero digits (e.g., input 1234.567)
      return `${formattedIntegerPart}.${decimalPart}`;
    }
  }

  // If digits is 0, just return the integer part
  return formattedIntegerPart;
};
